/**
 * Plan section 19 — Service history: what a customer was approved for, when it was done, and whether that
 * met the contract. Pure: the API reads one aggregation of stored Visit rows into `ServiceHistoryEntry`s
 * and these functions turn them into the rows the portal board and its CSV show.
 */
import { VisitStatus } from '../../enums';
import { Entity } from '../../entity';
import { EntityViewFilter } from '../../entities/views/entity.view.filter';
import { ScheduleFrequencyBand } from '../../../scheduling/schedule.rule';
import { EntityStatusType } from '../../entity.status/enum';
import { PlannedTaskPeriodTargetRule, PlannedTaskReportPeriod, PlannedTaskReportRule, PlannedTaskReportRuleKind, reportPeriodKey } from '../planned.task/planned.task.report.rule';
import { VisitApprovedTask } from './visit.approved.task';
import { VisitTaskCompletionStatus } from './visit.instance';
import { VISIT_SKIP_REASON_LABELS, VisitSkipReason } from './visit.skip.reason';
import { spacingGapDays } from './visit.spacing';
import type { ScheduleWeekCycle } from '../../../scheduling/schedule.rule';

export enum ServiceHistoryRowKind {
    Task = 'Task',
    Period = 'Period',
}

export enum ServiceHistoryResult {
    NotAssessed = 'Not assessed',
    Within = 'Within',
    Early = 'Early',
    Late = 'Late',
    Unresolved = 'Unresolved',
    Skipped = 'Skipped',
    Met = 'Met',
    Short = 'Short',
}

/**
 * Whether the task was resolved, independently of its timing or frequency assessment. Resolved means the Job's
 * task reached any closed status; which one is the row's `resolution`, so a tenant's own statuses need no case here.
 */
export enum ServiceHistoryCompletionStatus {
    Resolved = 'Resolved',
    Unresolved = 'Unresolved',
    Skipped = 'Skipped',
    Removed = 'Removed before service',
}

/** A row resolved without a readable Job task status still reads as done; every other state draws the board's hollow dot. */
const RESOLVED_WITHOUT_STATUS_COLOR = 'var(--wb-component-colors-utility-success-700)';

/** How a task's place on the Visit changed after approval; shown instead of judging work nobody approved. */
export enum ServiceHistoryChange {
    RemovedBeforeService = 'Approved, removed before service',
    AddedAfterApproval = 'Added after approval',
}

/** One task on one past stored Visit, as the history aggregation returns it. */
export interface ServiceHistoryEntry {
    visitId: string;
    localDate: string;
    timezone?: string;
    status?: VisitStatus;
    skipReasonCode?: VisitSkipReason;
    skipReason?: string;
    /** When the Visit was booked for (moved date or line window), as an instant. */
    approvedFor?: Date | string;
    /** Explicit appointment override only; absent when the Visit has not been moved. */
    rescheduledDate?: Date | string;
    site?: Entity;
    customer?: Entity;
    servicePlan?: Entity;
    serviceLine?: Entity;
    /** The Visit's Job, `name` holding its number as the Visit stores it; absent until the Job is built. */
    job?: Entity;
    task: VisitApprovedTask;
    /** Not on the approved set: served or completed on the Visit without being approved. */
    added?: boolean;
    /** Approved, but the Job was built without it. */
    removed?: boolean;
    /** `status` is absent on a completion stamped before its Task's status was kept on the Visit. */
    completions: { completedAt: Date | string; completedBy?: Entity; status?: VisitTaskCompletionStatus }[];
}

/** One task-occurrence board/CSV row; period fields describe its independent frequency assessment. Period rows also support legacy exports. */
export interface ServiceHistoryRow {
    _id: string;
    kind: ServiceHistoryRowKind;
    /** Task rows: the due date. Period rows: the period's first day. What the board sorts and the range filters by. */
    date: string;
    name: string;
    /** Source provenance, retained so combined readers never parse presentation ids to match work. */
    visitId?: string;
    taskId?: string;
    customer?: Entity;
    site?: Entity;
    /** All current Site zone names, displayed together without inventing a composite lookup id. */
    siteZone?: string;
    siteZoneIds?: string[];
    siteAddress?: string;
    /** Current standing task's plain cadence (`ScheduleEngine.frequencyBand`); absent when its source no longer exists. */
    frequency?: ScheduleFrequencyBand;
    /** Current standing task's compact schedule summary (`describeShort`), the Frequency cell's tooltip. */
    frequencyDescription?: string;
    /** Filter dimension resolved from the live Site, not a copied historical task attribute. */
    siteCategory?: Entity;
    zone?: Entity;
    serviceType?: Entity;
    serviceScope?: Entity;
    servicePlan?: Entity;
    serviceLine?: Entity;
    rule?: string;
    dueDate?: string;
    earliest?: string;
    latest?: string;
    approvedFor?: string;
    rescheduledDate?: string;
    /** Calendar days past the effective scheduled day, clamped to zero; never a tolerance assessment. */
    overdueDays?: number;
    /** The individual task occurrence's recorded resolution day. */
    completedOn?: string;
    /** Resolution of this occurrence only; frequency requirements never change this status. */
    completionStatus?: ServiceHistoryCompletionStatus;
    /**
     * Task rows: the status the Job's task was resolved with, in the tenant's name and colour. Until then, or when
     * the task is gone, it names the `completionStatus` instead, so the Resolution column always says something.
     */
    resolution?: Pick<VisitTaskCompletionStatus, '_id' | 'name' | 'color' | 'statusType'>;
    /** The reason chosen with that status, never the free-text note. */
    resolutionReason?: string;
    resolutionNotes?: string;
    completedBy?: Entity;
    /** Task rows only: the Job the Visit was served by. Period rows count several Visits, so they name none. */
    job?: Entity;
    period?: string;
    required?: number;
    completed?: number;
    completedDates?: string[];
    /** Days between consecutive completions inside the period. */
    gaps?: number[];
    change?: ServiceHistoryChange;
    result?: ServiceHistoryResult;
    /** The result in words: `Late (+4)`, `Short by 1`, `Met · spacing breach`, `Skipped (Customer request)`. */
    resultLabel?: string;
    /**
     * Task rows only: week A or B of the line's fortnight on the due date (WBS-12805). Present only when the line
     * has a fortnightly A/B task; derived on read by `fortnightWeekCycle`, never stored.
     */
    weekCycle?: ScheduleWeekCycle;
}

/**
 * One customer's, site's or plan's history between two inclusive local dates (YYYY-MM-DD); at least one
 * scope id is required, and several narrow together. `from` defaults to the start of the retention window;
 * `to` is clamped to today in `timezone`, including today's task occurrences but excluding future work.
 */
export interface ServiceHistoryRequest {
    customerId?: string;
    siteId?: string;
    /** Sites resolved from Site/Zone/Category filters, still narrowed by customer; null admits missing sites for empty/negative filters. */
    siteIds?: (string | null)[];
    /** Service lines resolved from the type/scope filters; null admits visits without a line for empty/negative filters. */
    serviceLineIds?: (string | null)[];
    servicePlanId?: string;
    from?: string;
    to?: string;
    timezone?: string;
    /** The board's view filters; only its Frequency conditions apply here, because a band is derived and stored nowhere. */
    filters?: EntityViewFilter[];
}

/** The history board's search: a case-insensitive match on the task, site or service line name. */
export function searchServiceHistoryRows(rows: ServiceHistoryRow[], search: string | undefined): ServiceHistoryRow[] {

    const term = search?.trim().toLowerCase();
    return term ? rows.filter(row => [row.name, row.site?.name, row.serviceLine?.name].some(value => value?.toLowerCase().includes(term))) : rows;
}

/** One row per task occurrence. Period progress is a separate assessment, never a substitute for task completion. */
export function serviceHistoryRows(entries: readonly ServiceHistoryEntry[], today?: string): ServiceHistoryRow[] {

    const periodEntries = entries.filter(entry => !entry.added && entry.task.reportRule?.kind === PlannedTaskReportRuleKind.PeriodTarget);
    const summaries = new Map(serviceHistoryPeriodRows(periodEntries).map(row => [row._id, row]));
    const taskRows = entries.map(entry => {
        const row = serviceHistoryTaskRow(entry, today);
        const rule = entry.task.reportRule;
        if (rule?.kind !== PlannedTaskReportRuleKind.PeriodTarget || row.change || row.completionStatus === ServiceHistoryCompletionStatus.Skipped) return row;
        const key = entry.task.periodKey ?? reportPeriodKey(entry.task.dueDate ?? entry.localDate, rule.period);
        const summary = summaries.get(`${entry.serviceLine?._id ?? ''}:${entry.task.taskId}:${key}`);
        if (!summary) return row;
        return {
            ...row,
            period: summary.period,
            required: summary.required,
            completed: summary.completed,
            completedDates: summary.completedDates,
            gaps: summary.gaps,
            result: summary.result,
            resultLabel: summary.resultLabel,
        };
    });

    return taskRows
        .sort((left, right) => right.date.localeCompare(left.date) || left.name.localeCompare(right.name) || left._id.localeCompare(right._id));
}

/**
 * Tolerance: a completion's local day against `[earliest, latest]` resolved when the row was stored, so
 * a reschedule never turns a late service "within". Completion is always explicit; a task without a rule is not assessed.
 */
function serviceHistoryTaskRow(entry: ServiceHistoryEntry, today?: string): ServiceHistoryRow {

    const timezone = entry.timezone || 'UTC';
    const completion = latestCompletion(entry.completions);
    const completedOn = completion ? localDateOf(completion.completedAt, timezone) : undefined;
    const rescheduledDate = entry.rescheduledDate ? localDateOf(entry.rescheduledDate, timezone) : undefined;
    const rule = entry.task.reportRule;
    let completionStatus = ServiceHistoryCompletionStatus.Unresolved;
    if (completedOn) completionStatus = ServiceHistoryCompletionStatus.Resolved;
    else if (entry.removed) completionStatus = ServiceHistoryCompletionStatus.Removed;
    else if (entry.status === 'Skipped') completionStatus = ServiceHistoryCompletionStatus.Skipped;
    const row: ServiceHistoryRow = {
        _id: `${entry.visitId}:${entry.task.taskId}`,
        kind: ServiceHistoryRowKind.Task,
        date: entry.task.dueDate ?? entry.localDate,
        name: entry.task.name,
        visitId: entry.visitId,
        taskId: entry.task.taskId,
        customer: entry.customer,
        completionStatus,
        site: entry.site,
        servicePlan: entry.servicePlan,
        serviceLine: entry.serviceLine,
        ...(rule && !entry.added ? { rule: describeReportRule(rule) } : {}),
        dueDate: entry.task.dueDate ?? entry.localDate,
        ...(rescheduledDate ? { rescheduledDate } : {}),
        ...(entry.task.toleranceFrom && !entry.added ? { earliest: entry.task.toleranceFrom, latest: entry.task.toleranceTo } : {}),
        ...(entry.approvedFor ? { approvedFor: localDateOf(entry.approvedFor, timezone) } : {}),
        ...(completedOn ? { completedOn } : {}),
        ...(completion?.completedBy ? { completedBy: completion.completedBy } : {}),
        resolution: completion?.status ? resolutionOf(completion.status) : resolutionStateOf(completionStatus),
        ...(resolutionReasonOf(completion?.status) ? { resolutionReason: resolutionReasonOf(completion?.status) } : {}),
        ...(completion?.status?.note && completion.status.statusType !== EntityStatusType.Active ? { resolutionNotes: completion.status.note } : {}),
        ...(entry.job?._id ? { job: entry.job } : {}),
    };
    const through = completedOn ?? today;
    if (through && !entry.removed && entry.status !== 'Skipped') {
        const effectiveDate = rescheduledDate ?? row.dueDate!;
        row.overdueDays = Math.max(0, Math.round((Date.parse(through) - Date.parse(effectiveDate)) / 86_400_000));
    }
    if (entry.added) return { ...row, change: ServiceHistoryChange.AddedAfterApproval, resultLabel: ServiceHistoryChange.AddedAfterApproval };
    if (entry.removed && !completedOn) return { ...row, change: ServiceHistoryChange.RemovedBeforeService, resultLabel: ServiceHistoryChange.RemovedBeforeService };

    return { ...row, ...taskResult(entry, completedOn) };
}

/**
 * Period target: completions counted in each calendar period of their local completion day, plus the gaps
 * between them inside the period. Periods are the ones an approved Visit was due in or a completion fell
 * in, so a period with approved work and nothing done reads Short rather than vanishing.
 */
function serviceHistoryPeriodRows(entries: readonly ServiceHistoryEntry[]): ServiceHistoryRow[] {

    const groups = new Map<string, { entries: ServiceHistoryEntry[]; rule: PlannedTaskPeriodTargetRule; dates: Set<string> }>();
    for (const entry of entries) {
        const rule = entry.task.reportRule as PlannedTaskPeriodTargetRule;
        const taskKey = `${entry.serviceLine?._id ?? ''}:${entry.task.taskId}`;
        const timezone = entry.timezone || 'UTC';
        const keys = [entry.task.periodKey ?? reportPeriodKey(entry.task.dueDate ?? entry.localDate, rule.period)];
        const completed = latestCompletion(entry.completions);
        const completedOn = completed ? localDateOf(completed.completedAt, timezone) : undefined;
        if (completedOn) keys.push(reportPeriodKey(completedOn, rule.period));
        for (const key of new Set(keys)) {
            const groupKey = `${taskKey}:${key}`;
            const group = groups.get(groupKey) ?? { entries: [], rule, dates: new Set<string>() };
            group.entries.push(entry);
            if (completedOn && reportPeriodKey(completedOn, rule.period) === key) group.dates.add(completedOn);
            groups.set(groupKey, group);
        }
    }

    return [...groups].map(([groupKey, group]) => periodRow(groupKey, group.entries, group.rule, [...group.dates].sort()));
}

/** "3 days before / 7 after", "2 per month · 10–18 days apart". */
function describeReportRule(rule: PlannedTaskReportRule): string {

    if (rule.kind === PlannedTaskReportRuleKind.Tolerance) return `${rule.daysBefore} ${dayWord(rule.daysBefore)} before / ${rule.daysAfter} after`;
    const spacing = rule.spacing;
    const apart = typeof spacing?.minDays === 'number' && typeof spacing?.maxDays === 'number'
        ? `${spacing.minDays}–${spacing.maxDays} days apart`
        : typeof spacing?.minDays === 'number'
            ? `at least ${spacing.minDays} ${dayWord(spacing.minDays)} apart`
            : typeof spacing?.maxDays === 'number'
                ? `at most ${spacing.maxDays} ${dayWord(spacing.maxDays)} apart`
                : undefined;

    return `${rule.count} ${rule.count === 1 ? 'completion' : 'completions'} per ${rule.period.toLowerCase()}${apart ? ` · ${apart}` : ''}`;
}

function taskResult(entry: ServiceHistoryEntry, completedOn: string | undefined): Pick<ServiceHistoryRow, 'result' | 'resultLabel'> {

    if (entry.status === 'Skipped' && !completedOn) {
        const reason = entry.skipReasonCode ? VISIT_SKIP_REASON_LABELS[entry.skipReasonCode] : entry.skipReason;
        return { result: ServiceHistoryResult.Skipped, resultLabel: reason ? `Skipped (${reason})` : ServiceHistoryResult.Skipped };
    }
    if (!entry.task.reportRule) return { result: ServiceHistoryResult.NotAssessed, resultLabel: 'Not applicable' };
    if (!completedOn) return { result: ServiceHistoryResult.NotAssessed, resultLabel: 'Awaiting resolution' };
    const { toleranceFrom, toleranceTo } = entry.task;
    if (!toleranceFrom || !toleranceTo) return { result: ServiceHistoryResult.NotAssessed, resultLabel: 'Not assessed' };
    if (completedOn < toleranceFrom) {
        const days = spacingGapDays(completedOn, toleranceFrom)!;
        return { result: ServiceHistoryResult.Early, resultLabel: `Early by ${days} ${dayWord(days)}` };
    }
    if (completedOn > toleranceTo) {
        const days = spacingGapDays(toleranceTo, completedOn)!;
        return { result: ServiceHistoryResult.Late, resultLabel: `Late by ${days} ${dayWord(days)}` };
    }

    return { result: ServiceHistoryResult.Within, resultLabel: 'On time' };
}

function periodRow(groupKey: string, entries: ServiceHistoryEntry[], rule: PlannedTaskPeriodTargetRule, dates: string[]): ServiceHistoryRow {

    const periodKey = groupKey.slice(groupKey.lastIndexOf(':') + 1);
    const first = entries[0];
    const gaps = dates.slice(1).map((date, index) => spacingGapDays(dates[index], date) ?? 0);
    const spacing = rule.spacing;
    const breach = gaps.some(gap => (typeof spacing?.minDays === 'number' && gap < spacing.minDays) || (typeof spacing?.maxDays === 'number' && gap > spacing.maxDays));
    const short = Math.max(0, rule.count - dates.length);
    const result = short ? ServiceHistoryResult.Short : ServiceHistoryResult.Met;
    // Keep progress scannable in the cell; the tooltip explains the recorded and required completions.
    const label = `${dates.length} of ${rule.count} this ${rule.period.toLowerCase()}`;

    return {
        _id: groupKey,
        kind: ServiceHistoryRowKind.Period,
        date: reportPeriodStart(periodKey, rule.period),
        name: first.task.name,
        site: first.site,
        servicePlan: first.servicePlan,
        serviceLine: first.serviceLine,
        rule: describeReportRule(rule),
        period: periodKey,
        required: rule.count,
        completed: dates.length,
        completedDates: dates,
        gaps,
        result,
        resultLabel: breach ? `${label} · spacing not met` : label,
    };
}

/** The first local day of a period key from `reportPeriodKey`. */
function reportPeriodStart(key: string, period: PlannedTaskReportPeriod): string {

    if (period === PlannedTaskReportPeriod.Month) return `${key}-01`;
    if (period === PlannedTaskReportPeriod.Quarter) return `${key.slice(0, 4)}-${String((Number(key.slice(-1)) - 1) * 3 + 1).padStart(2, '0')}-01`;
    const [year, week] = [Number(key.slice(0, 4)), Number(key.slice(-2))];
    const fourthOfJanuary = new Date(Date.UTC(year, 0, 4));
    const monday = new Date(fourthOfJanuary);
    monday.setUTCDate(fourthOfJanuary.getUTCDate() - ((fourthOfJanuary.getUTCDay() || 7) - 1) + (week - 1) * 7);

    return monday.toISOString().slice(0, 10);
}

function latestCompletion(completions: ServiceHistoryEntry['completions']): ServiceHistoryEntry['completions'][number] | undefined {

    return [...completions ?? []].sort((left, right) => new Date(right.completedAt).getTime() - new Date(left.completedAt).getTime())[0];
}

/** The reason picked with the status; a status that asks for none stamps a hidden default named after itself, which says nothing. */
function resolutionReasonOf(status: VisitTaskCompletionStatus | undefined): string | undefined {

    if (status?.statusType === EntityStatusType.Active) return undefined;
    const reason = status?.reason?.name;
    return reason && reason !== status.name ? reason : undefined;
}

/** The Resolution cell for a row with no Job task status to show: the state itself. */
function resolutionStateOf(state: ServiceHistoryCompletionStatus): ServiceHistoryRow['resolution'] {

    return {
        _id: state,
        name: state === ServiceHistoryCompletionStatus.Unresolved ? 'Open' : state,
        ...(state === ServiceHistoryCompletionStatus.Resolved ? { color: RESOLVED_WITHOUT_STATUS_COLOR } : {}),
    };
}

/** The chip the board draws; the reason has its own column. */
function resolutionOf(status: VisitTaskCompletionStatus): ServiceHistoryRow['resolution'] {

    return {
        _id: status._id,
        name: status.name,
        ...(status.color ? { color: status.color } : {}),
        ...(status.statusType ? { statusType: status.statusType } : {}),
    };
}

function localDateOf(value: Date | string, timezone: string): string {

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(value));
}

function dayWord(days: number): string {

    return days === 1 ? 'day' : 'days';
}
